<script setup lang="ts">
    import { ModelType } from '~~/app/base/schemas/BaseSchema';
    import EndpointFactory from '~~/app/factories/EndpointFactory';
    import ExpenseEndpoint from '~~/app/invoicing/endpoints/ExpenseEndpoint';
    import Expense from '~~/app/invoicing/models/Expense';
    import ExpenseSchema from '~~/app/invoicing/schemas/ExpenseSchema';

    const endpoint = EndpointFactory.make(ModelType.EXPENSES) as ExpenseEndpoint;
    const { contextMenu, onAction, onPromise } = useModelContextMenu();

    const destroy = async (model: Expense, payload: LoadingButtonClickedEventPayload) => {
        await payload.loadUntil(onPromise(endpoint.destroy(model.getId())));
    };

    const unlinkWorksite = async (model: Expense, payload: LoadingButtonClickedEventPayload) => {
        const schema = new ExpenseSchema({ attributes: {} });
        schema.id = model.getId();
        schema.addToOneRelationship('worksite', ModelType.WORKSITES, null);
        await payload.loadUntil(onPromise(endpoint.addInclude('worksite').update(schema)));
    };
</script>

<template>
    <ModelContextMenu
        v-slot="slotProps"
        ref="contextMenu"
        :model-type="Expense"
        :menu-height="280"
    >
        <a
            href="#"
            :class="$theme('contextmenu.link')"
            @click.prevent="onAction(() => navigateTo(`/invoicing/expenses/${slotProps.model?.id}`))"
        >
            <i
                class="fa-regular fa-eye"
                :class="$theme('contextmenu.icon')"
            ></i>
            <span>{{ $t('invoicing.expenses.actions.view_document') }}</span>
        </a>
        <a
            href="#"
            :class="$theme('contextmenu.link')"
            @click.prevent="onAction(() => slotProps.model?.edit())"
        >
            <i
                class="fa-pencil"
                :class="$theme('contextmenu.icon')"
            ></i>
            <span>{{ $t('actions.edit') }}</span>
        </a>
        <a
            v-if="!slotProps.model?.worksite"
            href="#"
            :class="$theme('contextmenu.link')"
            @click.prevent="onAction(() => useEvent('invoicing:expense:add-worksite-modal:open', slotProps.model))"
        >
            <i
                class="fa-regular fa-link"
                :class="$theme('contextmenu.icon')"
            ></i>
            <span>{{ $t('invoicing.expenses.actions.link_to_worksite') }}</span>
        </a>
        <a
            v-if="slotProps.model?.worksite"
            v-can="'worksite:worksite:view_any'"
            href="#"
            :class="$theme('contextmenu.link')"
            @click.prevent="
                slotProps.model.worksite.open();
                onAction();
            "
        >
            <i
                class="fa-regular fa-hard-hat"
                :class="$theme('contextmenu.icon')"
            ></i>
            <span>{{ $t('actions.open_worksite') }}</span>
        </a>
        <LoadingButton
            v-if="slotProps.model?.worksite"
            v-can="'worksite:worksite:view_any'"
            :class-name="$theme('contextmenu.link')"
            prevent-default
            @clicked="(payload) => unlinkWorksite(slotProps.model, payload)"
        >
            <i
                class="fa-regular fa-link-slash"
                :class="$theme('contextmenu.icon')"
            ></i>
            <span>{{ $t('actions.unlink_worksite') }}</span>
        </LoadingButton>
        <a
            v-if="!slotProps.model?.isPaid"
            href="#"
            :class="$theme('contextmenu.link')"
            @click.prevent="onAction(() => useEvent('invoicing:expense:mark-as-paid-modal:open', slotProps.model))"
        >
            <i
                class="fa-regular fa-square-check"
                :class="$theme('contextmenu.icon')"
            ></i>
            <span>{{ $t('model_events_mark_as_paid') }}</span>
        </a>
        <a
            v-if="slotProps.model?.pdf"
            :href="slotProps.model.pdf.url"
            target="_blank"
            :class="$theme('contextmenu.link')"
            @click="onAction()"
        >
            <i
                class="fa-regular fa-download"
                :class="$theme('contextmenu.icon')"
            ></i>
            <span>{{ $t('actions.download') }}</span>
        </a>
        <LoadingButton
            v-if="slotProps.model.isDeletable"
            v-can="'invoicing:expense:delete'"
            :class-name="$theme('contextmenu.link')"
            prevent-default
            @clicked="(payload) => destroy(slotProps.model, payload)"
        >
            <i
                class="fa-trash"
                :class="$theme('contextmenu.icon')"
            ></i>
            <span>{{ $t('actions.delete') }}</span>
        </LoadingButton>
    </ModelContextMenu>
</template>
